import { useCallback, useEffect, useId, useState } from "react";
import {
  basisBadge,
  changeText,
  createAccount,
  deleteAccount,
  emptyAccounts,
  capturedLabel,
  expiryRuleText,
  fetchPoints,
  lastCapture,
  groupAccounts,
  inlineInstruments,
  inlineLines,
  markInstrumentUsed,
  pointsFigure,
  recordBalance,
  revealInstrumentCode,
  revealMemberNumber,
  revertInstrument,
  reviewEvent,
  setAccountExpiry,
  toneClass,
  type AttentionItem,
  type ExpiryBasis,
  type ManageAccount,
  type PointsAccount,
  type PointsDto,
} from "../lib/pointsPage";
import { useCapture } from "../lib/captureContext";
import { Fold } from "../components/Fold";
import { Field } from "../components/Form";
import { Icon } from "../components/Icons";
import { PageTitle, SectionTitle } from "../components/Heading";
import { Sheet } from "../components/Sheet";
import { formatWhole } from "../lib/money";
import { monthDay } from "../lib/creditsPage";
import { fetchChanges, type PointsChange } from "../lib/pointsChanges";
import { WhatChanged } from "../components/WhatChanged";

/**
 * Points: the portfolio, laid out the way AwardWallet does it (approved
 * mockup, 2026-09-10). Accounts group by program kind under the server's
 * subtotal; each row carries balance, change, value, expiry and age, and
 * certificates sit under their program. Tapping a row opens its basis,
 * activity and Record balance, because a points balance with no provenance is
 * indistinguishable from a guess.
 *
 * Held events stay behind a banner above the portfolio: they are excluded
 * from every balance shown, so the banner is a number the page is not showing.
 *
 * A revealed member number lives in component state only. It is never written
 * to localStorage, put in a URL, or logged, and React drops it on unmount.
 */
export default function Points() {
  const [data, setData] = useState<PointsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<number, string>>({});
  const [show, setShow] = useState("All");
  const [openId, setOpenId] = useState<number | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [emptyOpen, setEmptyOpen] = useState(false);
  // Which sheet is up. Record carries the account it was opened from, so the
  // button in an account's detail arrives already pointed at that account.
  const [sheet, setSheet] = useState<
    { kind: "add" } | { kind: "record"; label?: string } | null
  >(null);

  const [changes, setChanges] = useState<PointsChange[]>([]);

  const load = useCallback(() => {
    // `stale` drops a response whose filter the user has already moved on
    // from, or two quick taps let the slower request win on arrival.
    let stale = false;
    // A failure here costs the card, never the page.
    fetchChanges()
      .then((c) => {
        if (!stale) setChanges(c);
      })
      .catch(() => {});
    fetchPoints(show)
      .then((d) => {
        if (stale) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (stale) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      stale = true;
    };
  }, [show]);

  useEffect(load, [load]);
  // A capture run writes balances, credits and offers, so when one finishes
  // while Wallet is open this page is stale. `finished` is a counter the bridge
  // bumps on the live-to-finished edge, so this fires once per run and never on
  // arrival at a page after a run that was already over.
  const { finished } = useCapture();
  useEffect(() => {
    if (finished > 0) return load();
  }, [finished, load]);


  // An attention item's button opens its row, which only exists after the
  // render that follows, so the scroll waits for that render.
  const [scrollTo, setScrollTo] = useState<number | null>(null);
  useEffect(() => {
    if (scrollTo === null) return;
    const el = document.getElementById(`points-account-${scrollTo}`);
    if (!el) return;
    el.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    setScrollTo(null);
  }, [scrollTo, data]);

  const act = (p: Promise<unknown>) =>
    p.then(load).catch((e: unknown) =>
      setError(e instanceof Error ? e.message : String(e)),
    );

  if (error && !data) {
    return (
      <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
        {error}
      </div>
    );
  }
  if (!data) return <div className="text-sm text-neutral-400">Loading…</div>;

  if (!data.has_programs) {
    return (
      <p className="text-sm text-neutral-400">
        No loyalty programs yet. They seed on app start.
      </p>
    );
  }

  const m = data.metrics;
  const groups = groupAccounts(data.accounts, data.kind_totals);
  const empty = emptyAccounts(data.accounts);
  // A label earns its place only when a program has more than one account.
  const perProgram: Record<string, number> = {};
  for (const a of data.accounts) {
    perProgram[a.row.program_name] = (perProgram[a.row.program_name] ?? 0) + 1;
  }
  const choiceFor = (id: number) =>
    Object.entries(data.account_choices).find(([, v]) => v === id)?.[0];
  const hasChoices = Object.keys(data.account_choices).length > 0;
  const attention = data.attention;
  const accountOf = (id: number) => data.accounts.find((a) => a.id === id);
  const openFromAttention = (id: number) => {
    const target = accountOf(id);
    // The current filter may be hiding the row; fall back to All so it exists.
    if (!target) setShow("All");
    else if (target.empty) setEmptyOpen(true);
    setOpenId(id);
    setScrollTo(id);
  };
  const saveExpiry = (id: number, day: string | null) =>
    setAccountExpiry(id, day)
      .then(() => {
        setFlash(day === null ? "Date cleared" : "Date saved");
        load();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  // One row renderer for the kind groups and the folded empty section.
  const renderRow = (a: PointsAccount) => (
    <AccountRowView
      key={a.id}
      account={a}
      showLabel={perProgram[a.row.program_name] > 1}
      open={openId === a.id}
      onToggle={() => setOpenId((id) => (id === a.id ? null : a.id))}
      onMarkUsed={(id) => act(markInstrumentUsed(id))}
      onUndoReading={(id) => act(reviewEvent(id, "rejected"))}
      onRevert={(id) => revertInstrument(id).then(load)}
      onRevealCode={(id) => revealInstrumentCode(id)}
      onSaveExpiry={(day) => saveExpiry(a.id, day)}
      today={data.today}
      onRecord={() => setSheet({ kind: "record", label: choiceFor(a.id) })}
    />
  );

  return (
    <section className="space-y-6">
      <PageTitle>Points</PageTitle>
      {/* The answer to "did last night's run land?", which had none on a phone:
          /refresh redirects there, because a phone loads no extensions. */}
      {capturedLabel(lastCapture(data), data.today) && (
        <p className="-mt-4 text-[12.5px] text-neutral-500">
          Balances read {capturedLabel(lastCapture(data), data.today)}
        </p>
      )}

      {flash && (
        <div className="rounded-md border border-green-900 bg-green-950/30 p-3 text-sm text-green-300">
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          {error}
        </div>
      )}

      {/* The two actions share one row under the total on a phone, instead of
          wrapping onto their own line under the filter (restyle, 2026-09-12). */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="hero-num text-[42px]">{formatWhole(m.portfolio_total)}</span>
          <span className="text-sm text-neutral-400">
            {m.account_count} accounts
            {m.stale_count > 0 && (
              <span className="text-amber-400"> · {m.stale_count} not recently updated</span>
            )}
            {m.unvalued > 0 && ` · ${m.unvalued} unvalued`}
          </span>
        </div>
        <div className="flex gap-2 *:flex-1 sm:*:flex-none">
          {hasChoices && (
            <button type="button" onClick={() => setSheet({ kind: "record" })} className="btn btn-ghost">
              Record balance
            </button>
          )}
          <button type="button" onClick={() => setSheet({ kind: "add" })} className="btn btn-primary">
            + Add account
          </button>
        </div>
      </div>

      <WhatChanged
        changes={changes}
        today={data.today}
        onError={(message) => setError(message)}
      />

      {attention.length > 0 && (
        <ExpiringStrip
          items={attention}
          today={data.today}
          figureFor={(id) => pointsFigure(accountOf(id)?.row.balance_text)}
          onOpen={openFromAttention}
        />
      )}

      {data.review_queue.total > 0 && (
        <section className="rounded-xl bg-neutral-900/60">
          <button
            type="button"
            onClick={() => setReviewOpen((o) => !o)}
            aria-expanded={reviewOpen}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm"
          >
            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-sky-400" />
            <span className="grow font-medium">
              Needs review ({data.review_queue.total})
            </span>
            <span className="hidden text-xs text-neutral-500 sm:inline">
              held out of every balance
            </span>
            <Icon name="chevron" size={16} className="chev shrink-0 text-neutral-500" />
          </button>
          {reviewOpen && (
            <ul className="border-t border-neutral-800/70 px-4">
              {data.review_queue.caption && (
                <li className="py-2 text-xs text-neutral-500">
                  {data.review_queue.caption}
                </li>
              )}
              {data.review_queue.items.map((item) => (
                <li
                  key={item.approve_key}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-neutral-800/70 py-2.5 text-sm last:border-b-0"
                >
                  <div className="min-w-0 grow">
                    <div className="truncate">
                      <strong>{item.program_name}</strong>
                      <span className="text-neutral-400"> · {item.account_label}</span>
                    </div>
                    <div className="truncate text-xs text-neutral-500">
                      {item.source} · {item.effective_date}
                    </div>
                  </div>
                  <span className="tabular-nums">{item.points}</span>
                  <button
                    onClick={() => act(reviewEvent(_idOf(item.reject_key), "rejected"))}
                    className="btn btn-ghost btn-sm"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => act(reviewEvent(_idOf(item.approve_key), "approved"))}
                    className="btn btn-sm"
                  >
                    Approve
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {data.cipher.caption && (
        <p className={`text-xs ${toneClass(data.cipher.caption[0])}`}>
          {data.cipher.caption[1]}
        </p>
      )}

      <div>
        {/* Filtering is server-side, through the same `visible_accounts` the
            API uses everywhere else. The highlight follows the LOCAL choice,
            so a failed request never leaves the old filter looking selected. */}
        <div
          role="group"
          aria-label="Show"
          // A scrolling chip row, so three filters never wrap on a phone.
          className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0"
        >
          {data.filters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={f === show}
              onClick={() => setShow(f)}
              className={`min-h-9 shrink-0 rounded-full border px-3.5 text-[13px] whitespace-nowrap ${
                f === show
                  ? "border-neutral-100 bg-neutral-100 font-semibold text-neutral-950"
                  : "border-neutral-800 bg-neutral-900 text-neutral-400 hover:text-neutral-200"
              }`}
            >
              {f}
              {f === "Expiring soon" && attention.length > 0 && (
                <>
                  {" "}
                  <span className={f === show ? "text-amber-700" : "text-amber-400"}>
                    {attention.length}
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {data.empty && (
        <p
          className={
            data.empty[0] === "success"
              ? "text-sm text-green-400"
              : "text-sm text-neutral-400"
          }
        >
          {data.empty[1]}
        </p>
      )}

      {groups.map((g) => (
        <section key={g.kind}>
          <div className="flex items-baseline gap-3 border-b border-neutral-700 pb-1.5">
            <SectionTitle>{g.kind}</SectionTitle>
            <span className="ml-auto text-sm tabular-nums text-neutral-300">
              {formatWhole(g.total)}
              {g.unvalued > 0 && (
                <span className="text-xs text-neutral-500"> + {g.unvalued} unvalued</span>
              )}
            </span>
          </div>
          <div className={`${ROW_COLS_MD} hidden gap-3 py-2 text-[11px] font-medium uppercase tracking-wider text-neutral-500 md:grid`}>
            <span>Program</span>
            <span>Status</span>
            <span className="text-right">Balance</span>
            <span className="text-right">Change</span>
            <span className="text-right">Value</span>
            <span className="text-right">Expires</span>
            <span className="text-right">Updated</span>
          </div>
          <ul>{g.accounts.map(renderRow)}</ul>
        </section>
      ))}

      {/* Balance exactly 0 and nothing live under it: folded below the groups
          so they stop pushing real balances down the page. Their kind's
          subtotal is the server's and still counts them (it adds $0). */}
      {empty.length > 0 && (
        <section>
          <Fold
            open={emptyOpen}
            onClick={() => setEmptyOpen((o) => !o)}
            label={`Empty accounts (${empty.length})`}
          />
          {emptyOpen && <ul>{empty.map(renderRow)}</ul>}
        </section>
      )}

      {/* Every account, never the filtered set: the filter narrows the rows
          above and must not take away the only way to reveal or delete. */}
      <section>
        <Fold
          open={manageOpen}
          onClick={() => setManageOpen((o) => !o)}
          label={`Programs & accounts (${data.manage.length})`}
        />
        {manageOpen && (
          <div className="space-y-2 pt-2">
            {data.manage.map((a) => (
              <AccountControls
                key={a.id}
                account={a}
                revealed={revealed[a.id]}
                onReveal={() =>
                  revealMemberNumber(a.id)
                    .then((n) => setRevealed((r) => ({ ...r, [a.id]: n })))
                    .catch((e: unknown) =>
                      setError(e instanceof Error ? e.message : String(e)),
                    )
                }
                onHide={() =>
                  setRevealed((r) => {
                    const next = { ...r };
                    delete next[a.id];
                    return next;
                  })
                }
                onDelete={() => act(deleteAccount(a.id))}
              />
            ))}
          </div>
        )}
      </section>

      {sheet?.kind === "record" && (
        <Sheet title="Record balance" onClose={() => setSheet(null)}>
          <RecordBalance
            choices={data.account_choices}
            today={data.today}
            initialLabel={sheet.label}
            onRecorded={(message) => {
              setSheet(null);
              setFlash(message);
              load();
            }}
          />
        </Sheet>
      )}
      {sheet?.kind === "add" && (
        <Sheet title="Add account" onClose={() => setSheet(null)}>
          <AddAccount
            programs={data.programs}
            onAdded={(message) => {
              setSheet(null);
              setFlash(message);
              load();
            }}
          />
        </Sheet>
      )}
    </section>
  );
}

/** `pts_ok_12` / `pts_no_12` -> 12. The server builds these keys. */
function _idOf(key: string): number {
  return Number(key.split("_").pop());
}

/**
 * The desktop columns, shared by the header and every row so they line up. A
 * literal on purpose: Tailwind only generates classes it finds spelled out in
 * the source, so a class assembled at runtime silently never exists.
 */
const ROW_COLS_MD =
  "md:grid-cols-[minmax(0,1.6fr)_7rem_7rem_5.5rem_5.5rem_8rem_5rem]";

function EliteChip({ text, className = "" }: { text: string; className?: string }) {
  return (
    <span
      className={`inline-block shrink-0 rounded-full border border-amber-900/70 px-2 text-[11px] leading-5 text-amber-200/80 ${className}`}
    >
      {text}
    </span>
  );
}

/**
 * One account: two lines on a phone, AwardWallet's table row from `md` up.
 * The phone and desktop cells are separate elements rather than one set
 * restyled, because the phone packs four facts into two lines.
 */
/**
 * A revealed booking code lives in component state only, like a revealed
 * member number: never written to storage, never put in a URL.
 */
function AccountRowView({
  account: a,
  showLabel,
  open,
  onToggle,
  onMarkUsed,
  onUndoReading,
  onRevert,
  onRevealCode,
  onRecord,
  onSaveExpiry,
  today,
}: {
  account: PointsAccount;
  showLabel: boolean;
  open: boolean;
  onToggle: () => void;
  onMarkUsed: (id: number) => void;
  /** Resolves with the decrypted booking code, which is held in state here
   *  and nowhere else. */
  onRevealCode: (id: number) => Promise<string>;
  onUndoReading: (eventId: number) => void;
  /** Rejects with the server's reason, shown beside the credit it refused. */
  onRevert: (id: number) => Promise<unknown>;
  onRecord: () => void;
  /** A YYYY-MM-DD date sets the expiry by hand; null clears it. */
  onSaveExpiry: (day: string | null) => Promise<unknown>;
  /** The expiry as a date, when the attention list carries one, to prefill
   *  the date field. The row itself only sends display text. */
  today: string;
}) {
  const [codes, setCodes] = useState<Record<number, string>>({});
  const revealCode = async (id: number) => {
    const code = await onRevealCode(id);
    setCodes((prev) => ({ ...prev, [id]: code }));
  };
  // A refused Revert's reason, shown where it was clicked rather than in the
  // banner. Keyed by the change it refused, so a newer change offers Revert again.
  const [revertErrors, setRevertErrors] = useState<Record<string, string>>({});
  const changeKey = (i: { used_key: string; revert?: { was: string; on: string } | null }) =>
    `${i.used_key}|${i.revert?.was}|${i.revert?.on}`;
  const r = a.row;
  const change = changeText(r.change);
  const changeTone = r.change && r.change > 0 ? "text-green-400" : "text-red-400";
  const active = inlineInstruments(a.instruments.items);
  const badge = basisBadge(r.basis);
  const value = r.value === null ? "—" : formatWhole(r.value);
  const ageClass = toneClass(r.age[0]) || "text-neutral-500";

  return (
    <li id={`points-account-${a.id}`} className="border-b border-neutral-800/70 text-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-0.5 py-2.5 text-left md:items-center md:gap-3 ${ROW_COLS_MD}`}
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="min-w-0 font-medium">
            {r.program_name}
            {showLabel && <span className="font-normal text-neutral-500"> · {r.label}</span>}
          </span>
          {r.elite_status && <EliteChip text={r.elite_status} className="md:hidden" />}
        </span>
        <span className="text-right tabular-nums md:hidden">{value}</span>
        <span className="text-xs tabular-nums text-neutral-400 md:hidden">
          {r.balance_text}
          {change && <span className={changeTone}> {change}</span>}
          {(r.expires || r.expires_basis === "never") && (
            <>
              {" · "}
              <ExpiryText expires={r.expires} basis={r.expires_basis} />
            </>
          )}
        </span>
        <span className={`text-right text-xs md:hidden ${ageClass}`}>
          {active.length > 0 && <span className="text-neutral-500">{active.length} cert · </span>}
          {r.age[1]}
        </span>

        <span className="hidden md:block">
          {r.elite_status && <EliteChip text={r.elite_status} />}
        </span>
        <span className="hidden text-right tabular-nums md:block">{r.balance_text}</span>
        <span className={`hidden text-right tabular-nums md:block ${change ? changeTone : "text-neutral-600"}`}>
          {change ?? "·"}
        </span>
        <span className="hidden text-right tabular-nums md:block">{value}</span>
        <span className="hidden text-right md:block">
          <ExpiryText expires={r.expires} basis={r.expires_basis} />
        </span>
        <span className={`hidden text-right md:block ${ageClass}`}>{r.age[1]}</span>
      </button>

      {inlineLines(a.instruments.items, a.instruments.totals).map((line) =>
        line.kind === "credit" ? (
          <div key={line.credit.used_key} className="flex items-baseline gap-2 pb-2 pl-4 text-xs">
            <span aria-hidden className="text-neutral-600">↳</span>
            <span className="min-w-0 grow truncate text-neutral-300">
              {line.credit.name}
              {line.credit.amount && <span className="text-neutral-500"> · {line.credit.amount}</span>}
            </span>
            <span className={`shrink-0 ${toneClass(line.credit.deadline[0]) || "text-neutral-500"}`}>
              {line.credit.deadline[1]}
            </span>
          </div>
        ) : (
          <div key={`total-${line.total.name}`} className="flex items-baseline gap-2 pb-2 pl-4 text-xs">
            <span aria-hidden className="text-neutral-600">↳</span>
            <span className="min-w-0 grow truncate text-neutral-300">
              {line.total.name}
              <span className="text-neutral-500"> · {line.total.total} in {line.total.count}</span>
            </span>
            <span className={`shrink-0 ${toneClass(line.total.next[0]) || "text-neutral-500"}`}>
              next {line.total.next[1]}
            </span>
          </div>
        ),
      )}

      {open && (
        <div className="mb-3 space-y-3 rounded-xl bg-neutral-900 p-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
            <span className={badge.className}>{badge.text}</span>
            {r.delta_text && <span className="text-neutral-400">{r.delta_text}</span>}
            {r.value_text ? (
              <span className="text-neutral-400">
                {r.value_text} {r.cpp_text}
              </span>
            ) : (
              r.no_valuation_reason && (
                <span className="text-neutral-500">{r.no_valuation_reason}</span>
              )
            )}
            {r.held_count > 0 && <span className="text-amber-400">{r.held_count} held</span>}
          </div>
          {r.error_caption && <p className="text-xs text-amber-400">{r.error_caption}</p>}
          {/* Undo lives here, one account at a time, and never as a
              page-level list: readings apply quietly, and only a big jump is
              held into Needs review (2026-09-16 mockup feedback). */}
          {a.capture && (
            <p className="flex flex-wrap items-baseline gap-x-3 text-xs text-neutral-400">
              <span>Read by browser capture {monthDay(a.capture.as_of, today)}</span>
              <button
                type="button"
                onClick={() => onUndoReading(a.capture!.event_id)}
                className="min-h-8 text-sky-300 hover:text-sky-200"
              >
                Undo this reading
              </button>
            </p>
          )}
          <ExpirationPanel
            expires={r.expires}
            basis={r.expires_basis}
            source={r.expires_source}
            note={r.expires_note ?? null}
            initialDay={r.expires_on ?? ""}
            onSave={onSaveExpiry}
          />

          {a.instruments.items.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-neutral-400">{a.instruments.title}</div>
              {a.instruments.totals.map((t) => (
                <div key={`total-${t.name}`} className="text-xs text-neutral-300">
                  {t.name}: <span className="font-semibold tabular-nums">{t.total}</span> in {t.count}
                </div>
              ))}
              {a.instruments.items.map((i) => (
                <div key={i.used_key}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  <span className="grow">{i.spent ? <s>{i.name}</s> : i.name}</span>
                  <span>{i.amount}</span>
                  <span className={toneClass(i.deadline[0]) || "text-neutral-500"}>
                    Book by {i.deadline[1]}
                  </span>
                  {i.travel && (
                    <span className={toneClass(i.travel[0]) || "text-neutral-500"}>
                      Travel by {i.travel[1]}
                    </span>
                  )}
                  {i.review_note && <span className="text-amber-400">{i.review_note}</span>}
                  {i.code_stored && (
                    codes[_idOf(i.used_key)] ? (
                      <span className="font-mono">{codes[_idOf(i.used_key)]}</span>
                    ) : (
                      <button
                        onClick={() => void revealCode(_idOf(i.used_key))}
                        className="btn btn-ghost btn-sm"
                      >
                        Show code
                      </button>
                    )
                  )}
                  {i.spent ? (
                    <span className="text-neutral-500">{i.status_text}</span>
                  ) : (
                    <button
                      onClick={() => onMarkUsed(_idOf(i.used_key))}
                      className="btn btn-ghost btn-sm"
                    >
                      Mark used
                    </button>
                  )}
                </div>
                {i.revert && revertErrors[changeKey(i)] ? (
                  <p className="text-xs text-amber-400">{revertErrors[changeKey(i)]}</p>
                ) : (
                  i.revert && (
                    <p className="flex flex-wrap items-baseline gap-x-3 text-xs text-neutral-500">
                      <span>
                        Was {i.revert.was} before the {monthDay(i.revert.on, today)} capture
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          onRevert(_idOf(i.used_key)).catch((e: unknown) =>
                            setRevertErrors((errs) => ({
                              ...errs,
                              [changeKey(i)]: e instanceof Error ? e.message : String(e),
                            })),
                          )
                        }
                        className="min-h-8 text-sky-300 hover:text-sky-200"
                      >
                        Revert
                      </button>
                    </p>
                  )
                )}
                </div>
              ))}
            </div>
          )}

          {(a.activity.recent.length > 0 || a.activity.superseded.length > 0) && (
            <div className="space-y-1 text-xs">
              <div className="font-medium text-neutral-400">Activity</div>
              {a.activity.recent.map((line) => (
                <p key={line} className="font-mono text-neutral-300">
                  {line.replace(/`/g, "")}
                </p>
              ))}
              {a.activity.superseded.map((line) => (
                <p key={line} className="text-neutral-500">
                  {line}
                </p>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={onRecord}
            className="btn btn-sm"
          >
            Record balance
          </button>
        </div>
      )}
    </li>
  );
}

/** The dashed "est." or solid "stated" marker beside an expiry date. */
function ExpiryChip({ basis }: { basis: "stated" | "estimated" }) {
  const border = basis === "estimated" ? "border-dashed border-neutral-500" : "border-neutral-600";
  return (
    <span
      className={`ml-1 inline-block rounded-full border px-[7px] align-[1px] text-[10.5px] leading-[18px] tracking-wide text-neutral-300 ${border}`}
    >
      {basis === "estimated" ? "est." : "stated"}
    </span>
  );
}

/** An account's expiry as its row shows it: the server's toned text, an
 *  "est." chip when estimated, grey "Never", or a dot when unknown. */
function ExpiryText({
  expires,
  basis,
}: {
  expires: PointsAccount["row"]["expires"];
  basis: ExpiryBasis;
}) {
  if (!expires) {
    return basis === "never" ? (
      <span className="text-neutral-600">Never</span>
    ) : (
      <span className="text-neutral-600">·</span>
    );
  }
  return (
    <>
      <span className={toneClass(expires[0])}>{expires[1]}</span>
      {basis === "estimated" && <ExpiryChip basis="estimated" />}
    </>
  );
}

/**
 * Balances about to expire, or whose stated date has passed with points still
 * on them. Above everything else and absent when nothing needs acting on
 * (approved mockup, 2026-09-19).
 */
function ExpiringStrip({
  items,
  today,
  figureFor,
  onOpen,
}: {
  items: AttentionItem[];
  today: string;
  figureFor: (accountId: number) => string | null;
  onOpen: (accountId: number) => void;
}) {
  return (
    <section
      aria-labelledby="points-expiring"
      className="rounded-xl border border-amber-500/35 bg-amber-500/[0.08]"
    >
      <div className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold">
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-amber-400" />
        <span id="points-expiring">Points expiring</span>
        <span className="ml-auto text-xs font-normal text-neutral-400">within 30 days</span>
      </div>
      <ul className="border-t border-amber-500/20 px-4">
        {items.map((i) => {
          const figure = figureFor(i.account_id);
          const date = monthDay(i.date, today);
          const box =
            i.past || i.days_left <= 7
              ? "bg-red-500/15 text-red-400"
              : "bg-amber-500/15 text-amber-400";
          return (
            <li
              key={i.account_id}
              data-testid="expiring-item"
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-amber-500/15 py-3 text-sm last:border-b-0"
            >
              <div className={`min-w-[58px] rounded-[9px] px-1 py-1.5 text-center leading-tight ${box}`}>
                <b className="block text-xl font-bold tabular-nums">
                  {i.past ? "!" : i.days_left}
                </b>
                <span className="text-[10px] uppercase tracking-[0.08em]">
                  {i.past ? "passed" : i.days_left === 1 ? "day" : "days"}
                </span>
              </div>
              <div className="min-w-0">
                <div className="font-semibold">
                  {i.program_name}
                  {i.account_label && (
                    <span className="font-normal text-neutral-400"> · {i.account_label}</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-neutral-400">
                  {i.past ? (
                    `Stated date ${date} has passed and the balance is still there. Recheck the program.`
                  ) : (
                    <>
                      {figure ? `${figure} points expire ${date}` : `Points expire ${date}`}
                      <ExpiryChip basis={i.basis} />
                    </>
                  )}
                </div>
              </div>
              <div className="col-span-2 col-start-2 sm:col-span-1 sm:col-start-auto">
                <button type="button" onClick={() => onOpen(i.account_id)} className="btn btn-ghost btn-sm">
                  {i.past ? "Update date" : "Details"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const EXPIRY_SOURCES: Record<string, string> = {
  browser: "Read from the program's page",
  manual: "Entered by hand",
  awardwallet: "From AwardWallet",
};

/**
 * How an account's expiry is known, and a hand-set date that latches over
 * captures until cleared. Only a manual date has a latch to clear.
 */
function ExpirationPanel({
  expires,
  basis,
  source,
  note,
  initialDay,
  onSave,
}: {
  expires: PointsAccount["row"]["expires"];
  basis: ExpiryBasis;
  source: string | null;
  note: string | null;
  initialDay: string;
  onSave: (day: string | null) => Promise<unknown>;
}) {
  const [day, setDay] = useState(initialDay);
  // Busy for the round trip, so a double tap cannot send two writes.
  const [busy, setBusy] = useState(false);
  const run = (value: string | null) => {
    if (busy) return;
    setBusy(true);
    void onSave(value).finally(() => setBusy(false));
  };
  const inputId = useId();

  return (
    <div className="space-y-2.5 rounded-xl bg-neutral-950/60 p-3.5">
      <h4 className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
        Expiration
      </h4>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-[13px]">
        <dt className="text-neutral-500">Rule</dt>
        <dd>
          {expiryRuleText(basis)}
          {note && <div className="text-xs text-neutral-500">{note}</div>}
        </dd>
        {expires && (
          <>
            <dt className="text-neutral-500">Date</dt>
            <dd>
              <ExpiryText expires={expires} basis={basis} />
            </dd>
          </>
        )}
        {source && EXPIRY_SOURCES[source] && (
          <>
            <dt className="text-neutral-500">From</dt>
            <dd>{EXPIRY_SOURCES[source]}</dd>
          </>
        )}
      </dl>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={inputId} className="text-xs text-neutral-500">
          Set the date yourself
        </label>
        <input
          id={inputId}
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="rounded-lg border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 font-mono text-[13px] text-neutral-100 [color-scheme:dark]"
        />
        <button
          type="button"
          disabled={busy || !day}
          onClick={() => run(day)}
          className="btn btn-primary btn-sm"
        >
          Save
        </button>
        {source === "manual" && (
          <button type="button" disabled={busy} onClick={() => run(null)} className="btn btn-ghost btn-sm">
            Use captured date
          </button>
        )}
      </div>
      <p className="text-xs text-neutral-500">
        A date you set wins over captures until you clear it. Use it after you earn or redeem,
        before the next capture sees the change.
      </p>
    </div>
  );
}

/**
 * Reveal and delete, for EVERY account, never the filtered set.
 *
 * Its own section because the live page has one: the balance cards above
 * honour the "Show" radio and the "Programs & accounts" expander below them
 * does not. Folding these controls into the filtered card meant that choosing
 * "Expiring soon" removed the only way to reveal a member number or delete an
 * account for everything the filter excluded.
 *
 * A revealed number lives in the parent's component state for a single render
 * and is never written to localStorage, put in a URL, or logged.
 */
function AccountControls({
  account,
  revealed,
  onReveal,
  onHide,
  onDelete,
}: {
  account: ManageAccount;
  revealed: string | undefined;
  onReveal: () => void;
  onHide: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-neutral-800 p-3 text-sm">
      <span className="font-semibold">
        {account.program_name} · {account.label}
      </span>
      <code className="text-xs">{revealed ?? account.masked}</code>
      {account.member_number_status && (
        <span className={`text-xs ${toneClass(account.member_number_status[0])}`}>
          {account.member_number_status[1]}
        </span>
      )}
      {revealed ? (
        <button onClick={onHide} className="btn btn-ghost btn-sm">
          Hide
        </button>
      ) : (
        <button
          onClick={onReveal}
          disabled={account.reveal_disabled}
          className="btn btn-ghost btn-sm"
        >
          Reveal
        </button>
      )}
      <span className="grow" />
      {confirming ? (
        <>
          <span className="text-xs text-red-400">Delete this account?</span>
          <button
            onClick={onDelete}
            className="btn btn-destroy btn-sm"
          >
            Yes, delete
          </button>
          <button
            onClick={() => setConfirming(false)}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="btn btn-danger btn-sm"
        >
          Delete
        </button>
      )}
    </div>
  );
}

function RecordBalance({
  choices,
  today,
  initialLabel,
  onRecorded,
}: {
  choices: Record<string, number>;
  today: string;
  initialLabel?: string;
  onRecorded: (message: string) => void;
}) {
  const labels = Object.keys(choices);
  const [label, setLabel] = useState(initialLabel ?? labels[0] ?? "");
  const [observed, setObserved] = useState("0");
  const [asOf, setAsOf] = useState(today);
  const [note, setNote] = useState("");
  // Busy for the round trip: a second tap would record a second reading.
  const [busy, setBusy] = useState(false);
  // Shown inside the sheet, where the eye is. The page's banner sits behind it.
  const [err, setErr] = useState<string | null>(null);

  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        recordBalance({
          account_id: choices[label],
          observed: Number(observed),
          as_of: asOf,
          note: note.trim() || undefined,
        })
          .then(onRecorded)
          .catch((x: unknown) => setErr(x instanceof Error ? x.message : String(x)))
          .finally(() => setBusy(false));
      }}
    >
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Account
        <select
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        >
          {labels.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <Field label="Balance" value={observed} onChange={setObserved} type="number" />
      <Field label="As of" value={asOf} onChange={setAsOf} type="date" />
      <Field label="Note" value={note} onChange={setNote} />
      {err && <p className="text-sm text-red-400">{err}</p>}
      <button
        type="submit"
        disabled={busy}
        className="btn btn-primary"
      >
        {busy ? "Recording…" : "Record"}
      </button>
    </form>
  );
}

function AddAccount({
  programs,
  onAdded,
}: {
  programs: Record<string, number>;
  onAdded: (message: string) => void;
}) {
  const names = Object.keys(programs);
  const [program, setProgram] = useState(names[0] ?? "");
  const [label, setLabel] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        createAccount({
          program_id: programs[program],
          label,
          member_number: number.trim() || undefined,
        })
          .then((r) => onAdded(r.message ?? "Account added."))
          .catch((x: unknown) => setErr(x instanceof Error ? x.message : String(x)))
          .finally(() => {
            // Cleared either way: a member number must not sit in a form field
            // after the request that carried it.
            setNumber("");
            setBusy(false);
          });
      }}
    >
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Program
        <select
          value={program}
          onChange={(e) => setProgram(e.target.value)}
          className="min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        >
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <Field label="Label" value={label} onChange={setLabel} />
      <Field label="Member number (optional)" value={number} onChange={setNumber} />
      {err && <p className="text-sm text-red-400">{err}</p>}
      <button
        type="submit"
        disabled={busy}
        className="btn btn-primary"
      >
        {busy ? "Adding…" : "Add account"}
      </button>
    </form>
  );
}
